/**
 * PLAT-TOS-LAYERED-1 — /terms/:product, the per-product terms addendum.
 *
 * PLACEHOLDER, pending legal review. White-label: this page names only the
 * product and "the platform", never the operator's brand. The platform Terms
 * page (/terms, TermsOfServicePage) is deliberately left unchanged.
 */
import { Link, useParams } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { PRODUCT_TERMS_LABELS, isTermsProduct } from '@/lib/productTerms'

export function ProductTermsPage() {
  const { product } = useParams<{ product: string }>()

  if (!isTermsProduct(product)) {
    return (
      <div className="min-h-screen bg-white">
        <div className="mx-auto max-w-3xl px-6 py-12 text-gray-700">
          <h1 className="text-2xl font-semibold text-gray-900">Product terms not found</h1>
          <p className="mt-2">There are no product terms at this address.</p>
          <Link to="/terms" className="mt-4 inline-block text-blue-600 hover:underline">
            Terms of Service
          </Link>
        </div>
      </div>
    )
  }

  const label = PRODUCT_TERMS_LABELS[product]

  return (
    <div className="min-h-screen bg-white">
      <div className="bg-gradient-to-r from-blue-50 to-blue-100 border-b border-blue-200 px-6 py-8">
        <div className="max-w-3xl mx-auto">
          <Link to="/" className="flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-4">
            <ArrowLeft className="h-4 w-4" />
            Back to platform
          </Link>
          <h1 className="text-3xl font-bold text-gray-900">{label} Terms</h1>
          <p className="text-gray-600 mt-2">Product addendum to the Terms of Service</p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-12 space-y-8 text-gray-700">
        <p
          className="font-semibold text-amber-800 bg-amber-50 border border-amber-300 rounded-lg p-4"
          data-testid="product-terms-draft-banner"
        >
          DRAFT - PENDING LEGAL
        </p>

        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-3">About these terms</h2>
          <p>
            These terms apply to your use of {label}. They add to the{' '}
            <Link to="/terms" className="text-blue-600 hover:underline">Terms of Service</Link>
            {' '}you accepted when you first signed in; they do not replace them. Where the two
            conflict on a matter specific to {label}, these terms apply.
          </p>
        </section>

        <section>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Placeholder</h3>
          <p>
            The full {label} terms are being prepared and will appear here once reviewed. When
            they change, you will be asked to review and accept the new version before you
            continue using {label}.
          </p>
        </section>
      </div>
    </div>
  )
}
